import type { ApiErrorBody } from "../../shared/api-types";

/** A failed API call, carrying the server's code, message, per-field messages and structured details. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fields: Record<string, string> = {},
    readonly details: unknown = undefined,
  ) {
    super(message);
  }
}

const NETWORK_MESSAGE = "Nu am putut contacta serverul. Verifică conexiunea și încearcă din nou.";

export async function api<T>(
  method: "GET" | "POST" | "PATCH" | "PUT" | "DELETE",
  path: string,
  body?: unknown,
  headers: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<T> {
  // A Blob body is a binary upload: sent as-is with the header the server's CSRF guard requires (DEC-040).
  const binary = body instanceof Blob;
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(body === undefined
          ? {}
          : binary
            ? { "Content-Type": body.type, "X-Meniova-Upload": "1" }
            : { "Content-Type": "application/json" }),
        ...headers,
      },
      body: body === undefined ? undefined : binary ? body : JSON.stringify(body),
      signal,
    });
  } catch {
    throw new ApiRequestError(0, "network", NETWORK_MESSAGE);
  }
  if (res.status === 204) return undefined as T;
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* non-JSON response */
  }
  if (!res.ok) {
    const err = (data as ApiErrorBody | null)?.error;
    throw new ApiRequestError(
      res.status,
      err?.code ?? "http_error",
      err?.message ?? "A apărut o eroare neașteptată. Încearcă din nou.",
      err?.fields ?? {},
      err?.details,
    );
  }
  return data as T;
}
